/**
 * Stage S24 — the two output document templates.
 *
 * Spec: docs/spec/00-MASTER-SPEC.md §3.13 (R-321, R-322), G-26, G-27, DF-19.
 *
 * The converter itself returns only `{ html, css }`. Those two strings are then
 * wrapped for delivery in exactly two places, and the port reproduces both:
 *
 *   • `generateFullHtml`   —. The standalone `.html` download
 *                            (ConverterApp.tsx:133 and :175).
 *   • `generatePreviewHtml` — HtmlPreview.tsx:20-35. The live preview iframe, written with
 *                            `doc.open(); doc.write(...); doc.close()` on every `[html, css]`
 *                            change — i.e. on every keystroke, which is why the preview loses
 *                            its scroll position (R-322).
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * THE ONLY DIFFERENCE between the two is the `<title>` line (verification log §2.2 #25).
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * They are written here as two independent literals because they are two independent
 * literals in the source. `templates.test.ts` pins the relationship — preview === standalone
 * minus the title line — so the two cannot drift apart unnoticed.
 *
 * Defects reproduced deliberately (DO NOT FIX — CLAUDE.md §3):
 *
 *   • DF-19 / G-26  `lang="en"` on a French document. Both templates. `settings.language`
 *                   is parsed from `\usepackage[french]{babel}` (R-034) and never used.
 *   • DF-19 / G-26  the standalone download hard-codes `<title>LaTeX Document</title>`;
 *                   `documentMetadata.title` is captured at S4 and never reaches the title.
 *                   The preview has no `<title>` at all.
 *   • DF-19 / G-27  both link KaTeX **0.16.9** from jsDelivr while the source app's
 *                   package.json pins `^0.16.25` (and L2HC ships 0.16.47). The stylesheet
 *                   and the rendering engine can therefore disagree.
 *   • R-321         FIXED 2026-08-30. The page-break separator used to be
 *                   `<img src="/divider.svg">`, root-absolute — so every page break in a
 *                   downloaded file rendered as a broken image. It was worse than that
 *                   note said: the file never existed on the server either. It is now an
 *                   inline SVG (`PAGE_BREAK_ORNAMENT` in commands-text.ts), so a saved or
 *                   library-hosted copy renders it with nothing to fetch. **The converter
 *                   now emits no `<img>` at all**, and nothing here needs rebasing.
 *   • DF-9          `content` is interpolated raw. Nothing is HTML-escaped anywhere on the
 *                   live path, so document text containing markup becomes live markup in
 *                   both the download and the iframe. This is a stored-XSS surface in a
 *                   shared workspace (CLAUDE.md §5). Keeping it is the port's job; fixing
 *                   it is DF-9's own commit.
 *
 * Neither template emits a `<meta name="color-scheme">`, a CSS reset, or `src/index.css`
 * (R-322) — the iframe is a bare document, which is what makes `.marginpar`'s content-box
 * arithmetic work by coincidence (C-2).
 */

/**
 * Standalone downloadable document —, R-321.
 *
 * Byte contract: no trailing newline; `css` and `content` each sit on their own line,
 * unindented, inside `  <style>` / `  </style>` and `  <article class="latex-document">` /
 * `  </article>` respectively.
 */
export function generateFullHtml(content: string, css: string): string {
  // PREAMBLE LINK: \usepackage[french]{babel} (preambuleV3.tex:19) — "Mise en forme aux normes
  // graphiques du français". PARSED, THEN CONTRADICTED. settings.ts R-034 reads the option list
  // into settings.language ("french"), and nothing ever reads it back: `lang="en"` below is
  // hard-coded, so every French translation is published declaring itself English (DF-19 / G-26).
  // That is not cosmetic — it is what a screen reader and the browser's own hyphenation dictionary
  // go by, and it compounds DF-12 (the `hyphens: auto` in css.ts the preamble asked to suppress).
  // The same line is hard-coded again in generatePreviewHtml below; fix both together, and update
  // the CONVERTISSEUR note on \usepackage[french]{babel} when you do.
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>LaTeX Document</title>
  <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.css">
  <style>
${css}
  </style>
</head>
<body>
  <article class="latex-document">
${content}
  </article>
</body>
</html>`
}

/**
 * Preview iframe document — HtmlPreview.tsx:20-35, R-322.
 *
 * Identical to `generateFullHtml` except that the `<title>` line is absent. Note the
 * argument order matches `generateFullHtml`, not the source's local variable order: the
 * source builds this inline from `html` and `css` props, so there is no call signature to
 * preserve. `(content, css)` keeps the two call sites interchangeable.
 */
export function generatePreviewHtml(content: string, css: string): string {
  // PREAMBLE LINK: \usepackage[french]{babel} (preambuleV3.tex:19) — the second hard-coded
  // `lang="en"`. See the note on generateFullHtml above; the two must change together.
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.css">
  <style>
${css}
  </style>
</head>
<body>
  <article class="latex-document">
${content}
  </article>
</body>
</html>`
}
